/**
 * Activités qu'une sortie d'AGORA couperait : un appel du softphone, un enregistrement Scribe. Un fournisseur les
 * déclare par useActiviteEnCours ; tant qu'il en reste une, fermer l'onglet ou recharger la page demande confirmation
 * (alerte du navigateur), et useQuitterAgora (pages/espace.tsx) demande confirmation avant toute redirection hors
 * d'AGORA (connexion Microsoft 365, vérification d'identité en pleine page, déconnexion), comme pour un brouillon.
 */
import React from 'react';

export type ActiviteEnCours = 'appel' | 'enregistrement';

const ACTIVITES = new Map<symbol, ActiviteEnCours>();
/**
 * Sortie confirmée dans AGORA : l'alerte du navigateur (beforeunload) ne la redemande pas. La redirection part tout de
 * suite après la confirmation ; passé ce délai, une fermeture d'onglet redemande confirmation.
 */
const DELAI_SORTIE_MS = 2000;
let sortieConfirmeeLe = Number.NEGATIVE_INFINITY;

export const autoriserSortie = (): void => { sortieConfirmeeLe = Date.now(); };
export const sortieAutorisee = (): boolean => Date.now() - sortieConfirmeeLe < DELAI_SORTIE_MS;

/** Activités en cours, sans doublon, dans l'ordre de déclaration. */
export const activitesEnCours = (): ActiviteEnCours[] => [...new Set(ACTIVITES.values())];

const avantDeQuitter = (e: BeforeUnloadEvent) => {
  if (!ACTIVITES.size || sortieAutorisee()) return;
  e.preventDefault();
  e.returnValue = '';
};

/** Déclare une activité en cours (appel décroché, enregistrement lancé) tant que `actif` est vrai. */
export function useActiviteEnCours(activite: ActiviteEnCours, actif: boolean): void {
  React.useEffect(() => {
    if (!actif) return;
    const id = Symbol(activite);
    ACTIVITES.set(id, activite);
    if (ACTIVITES.size === 1) window.addEventListener('beforeunload', avantDeQuitter);
    return () => {
      ACTIVITES.delete(id);
      if (!ACTIVITES.size) window.removeEventListener('beforeunload', avantDeQuitter);
    };
  }, [activite, actif]);
}
